/**
 * Attach an HLS manifest to a `<video>`, the way the watch page does it. hls.js
 * (Media Source Extensions) goes first, because a browser can say it plays HLS
 * natively and then fail to (hls.js documents Chrome doing exactly that); the
 * element's own player is the fallback for Safari-family browsers without MSE.
 * hls.js is already a dependency and a code-split chunk, so a board with no
 * recording never fetches it.
 *
 * `token` is the signed media token the gated media host wants: it rides the
 * first request as `?t=` and comes back as the `fw_media` cookie, which every
 * later segment request then carries (withCredentials / use-credentials).
 */
interface HlsHandle {
  destroy(): void;
}

export function mediaUrl(url: string, token?: string | null): string {
  if (!token) return url;
  return `${url}${url.includes("?") ? "&" : "?"}t=${encodeURIComponent(token)}`;
}

export function attachHls(
  video: HTMLVideoElement,
  url: string,
  token?: string | null,
  opts?: { lowest?: boolean },
): HlsHandle {
  let instance: { destroy: () => void } | null = null;
  let cancelled = false;
  const src = mediaUrl(url, token);

  const native = () => {
    if (token) video.crossOrigin = "use-credentials";
    video.src = src;
  };

  void import("hls.js")
    .then(({ default: Hls }) => {
      if (cancelled) return;
      if (Hls.isSupported()) {
        // Why: the frame grabber wants the cheapest rendition there is. hls.js
        // sorts levels by resolution, so 0 is the smallest one the manifest has,
        // and a 240p segment is plenty for an 84 pixel slot. `startLevel` picks
        // the first fragment; the other two keep every later seek down there too.
        const hls = new Hls({
          enableWorker: true,
          ...(opts?.lowest ? { startLevel: 0 } : {}),
          ...(token
            ? { xhrSetup: (xhr: XMLHttpRequest) => { xhr.withCredentials = true; } }
            : {}),
        });
        hls.loadSource(src);
        hls.attachMedia(video);
        if (opts?.lowest) {
          hls.autoLevelCapping = 0;
          hls.currentLevel = 0;
        }
        instance = hls;
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        native();
      }
    })
    .catch(() => {
      if (!cancelled && video.canPlayType("application/vnd.apple.mpegurl")) native();
    });

  return {
    destroy() {
      cancelled = true;
      instance?.destroy();
    },
  };
}
